import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ADMIN_MODULES = [
  { key:'dashboard',           label:'Dashboard',            icon:'🏠', desc:'Admin home with stats and charts' },
  { key:'students',            label:'Students',             icon:'👥', desc:'View and manage students' },
  { key:'studentLeft',         label:'Left Students',        icon:'🚪', desc:'View students who have left' },
  { key:'teachers',            label:'Teachers',              icon:'🧑‍🏫', desc:'View and manage teachers' },
  { key:'staffExits',          label:'Staff Exits',           icon:'🚪', desc:'View and request staff exits across all roles' },
  { key:'coordinators',        label:'Coordinators',          icon:'🧑‍💼', desc:'Manage coordinator accounts' },
  { key:'financeMembers',      label:'Finance Members',       icon:'💰', desc:'Manage finance accounts' },
  { key:'parentAccounts',      label:'Parent Accounts',       icon:'👪', desc:'Manage parent accounts' },
  { key:'classes',             label:'Classes',               icon:'🏫', desc:'View and manage classes' },
  { key:'timetable',           label:'Timetable',             icon:'🕐', desc:'View and manage timetable' },
  { key:'teacherTimetable',    label:'Teacher Timetable',     icon:'🕐', desc:'View teacher timetable' },
  { key:'datesheets',          label:'Datesheets',            icon:'🗓️', desc:'Manage exam datesheets' },
  { key:'assessmentTypes',     label:'Assessment Types',      icon:'📝', desc:'Manage assessment types' },
  { key:'assessments',         label:'Assessments',           icon:'📝', desc:'View and manage assessments' },
  { key:'gradeReports',        label:'Grade Reports',         icon:'📊', desc:'View student grade reports' },
  { key:'classPromotion',      label:'Class Promotion',       icon:'🎓', desc:'Promote students to next class' },
  { key:'yearEndCleanup',      label:'Year-End Cleanup',      icon:'🗑️', desc:'Archive year-end data' },
  { key:'ilp',                 label:'ILP',                   icon:'📋', desc:'Individual learning plans' },
  { key:'cloManagement',       label:'CLO Management',        icon:'📈', desc:'Manage course learning outcomes' },
  { key:'attainment',          label:'CLO Attainment',        icon:'📈', desc:'View CLO attainment' },
  { key:'ploManagement',       label:'PLO Management',        icon:'📈', desc:'Manage program learning outcomes' },
  { key:'ploAttainment',       label:'PLO Attainment',        icon:'📈', desc:'View PLO attainment' },
  { key:'studentAttendance',   label:'Student Attendance',    icon:'✅', desc:'Mark and view student attendance' },
  { key:'staffAttendance',     label:'Staff Attendance',      icon:'✅', desc:'View staff attendance records' },
  { key:'teacherAttendance',   label:'Teacher Attendance',    icon:'📅', desc:'View teacher attendance records' },
  { key:'biometric',           label:'Biometric',             icon:'🔒', desc:'Manage biometric and RFID devices' },
  { key:'studentGateLog',      label:'Student Gate Log',      icon:'🪪', desc:'View student RFID gate logs' },
  { key:'staffSalary',         label:'Staff Salary',          icon:'📊', desc:'View and manage staff salary records' },
  { key:'staffLeaves',         label:'Staff Leaves',          icon:'📋', desc:'Review staff leave requests' },
  { key:'staffManagement',     label:'Staff Management',      icon:'🏢', desc:'Manage staff accounts' },
  { key:'holidays',            label:'Holidays',              icon:'📆', desc:'View and manage holidays' },
  { key:'salaryRecords',       label:'Salary Records',        icon:'📊', desc:'View salary records' },
  { key:'leaves',              label:'Leaves',                icon:'🏖️', desc:'Manage leave requests' },
  { key:'fees',                label:'Fees',                  icon:'💵', desc:'Manage student fee bills' },
  { key:'expenses',            label:'Expenses',              icon:'💲', desc:'Track school expenses' },
  { key:'assets',              label:'Assets',                icon:'📦', desc:'Track school assets' },
  { key:'materials',           label:'Materials',             icon:'📚', desc:'Manage teaching materials' },
  { key:'notices',             label:'Notices',               icon:'📢', desc:'View and post notices' },
  { key:'feedback',            label:'Feedback',              icon:'📈', desc:'View feedback results' },
  { key:'accessPanel',         label:'Access Panel',          icon:'🛡️', desc:'Manage teacher/student/finance/coordinator access' },
  { key:'coordinatorRequests', label:'Coordinator Requests',  icon:'📥', desc:'Review coordinator requests' },
  { key:'financeRequests',     label:'Finance Requests',      icon:'📥', desc:'Review finance requests' },
  { key:'settings',            label:'Settings',              icon:'⚙️', desc:'School settings' },
  { key:'myAttendance',        label:'My Attendance',         icon:'📅', desc:'Own check-in/out and attendance' },
  { key:'mySalary',            label:'My Salary',             icon:'💰', desc:'View own salary and history' },
  { key:'myLeaves',            label:'My Leaves',             icon:'🏖️', desc:'Apply and manage own leaves' },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .oaa * { box-sizing:border-box; }
  .oaa { font-family:'Sora',sans-serif; color:#1a1d2e; animation:oaaFade .4s ease both; }
  @keyframes oaaFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .oaa-back { display:inline-flex; align-items:center; gap:6px; background:none; border:none; color:#7c3aed; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; margin-bottom:.8rem; padding:0; }
  .oaa-back svg { width:15px; height:15px; }
  .oaa-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .oaa-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .oaa-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .sub-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .sub-tab  { padding:6px 16px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; transition:all .15s; color:#6b7194; }
  .sub-tab:hover  { border-color:#7c3aed; color:#7c3aed; }
  .sub-tab.active { background:#7c3aed; color:white; border-color:#7c3aed; }
  .search-wrap { position:relative; flex:1; min-width:200px; margin-bottom:1.2rem; }
  .search-wrap svg { position:absolute; left:10px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; stroke-width:2; }
  .search-input { width:100%; padding:9px 12px 9px 34px; border:1.5px solid #e0e4f0; border-radius:10px; font-family:'Sora',sans-serif; font-size:.82rem; background:#f8f9fc; color:#1a1d2e; outline:none; }
  .search-input:focus { border-color:#7c3aed; background:white; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 14px; border:none; border-radius:9px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:14px; height:14px; stroke-width:2; }
  .btn-purple { background:#7c3aed; color:white; } .btn-purple:hover { background:#6d28d9; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:5px 10px; font-size:.73rem; } .btn:disabled { opacity:.5; cursor:not-allowed; }
  .role-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1rem; }
  .role-card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:1rem; flex-wrap:wrap; gap:8px; }
  .role-card-title { font-size:.9rem; font-weight:700; color:#0f1123; }
  .role-card-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .module-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:10px; }
  .module-item { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-radius:10px; border:1.5px solid #e8ebf4; background:#fafbff; gap:8px; transition:all .15s; }
  .module-item.enabled  { border-color:#10c98e; background:#f0fdf8; }
  .module-item.disabled { border-color:#e8ebf4; background:#fafbff; opacity:.75; }
  .module-item-left { display:flex; align-items:center; gap:10px; min-width:0; }
  .module-icon  { font-size:1.1rem; flex-shrink:0; }
  .module-label { font-size:.8rem; font-weight:600; color:#0f1123; }
  .module-desc  { font-size:.68rem; color:#9094a8; margin-top:1px; }
  .toggle { position:relative; width:40px; height:22px; flex-shrink:0; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; cursor:pointer; inset:0; background:#d1d5db; border-radius:99px; transition:.2s; }
  .toggle-slider:before { content:''; position:absolute; width:16px; height:16px; left:3px; top:3px; background:white; border-radius:50%; transition:.2s; }
  .toggle input:checked + .toggle-slider { background:#10c98e; }
  .toggle input:checked + .toggle-slider:before { transform:translateX(18px); }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .person-row { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; }
  .person-row:last-child { border-bottom:none; } .person-row:hover { background:#fafbff; }
  .p-avatar { width:32px; height:32px; border-radius:9px; background:#f5f3ff; color:#7c3aed; display:flex; align-items:center; justify-content:center; font-size:.68rem; font-weight:700; flex-shrink:0; }
  .p-name { font-size:.82rem; font-weight:600; color:#0f1123; }
  .p-meta { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .p-badge { display:inline-flex; font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .p-badge.override { background:#fef3c7; color:#92400e; }
  .p-badge.default  { background:#f0f2f7; color:#6b7280; }
  .override-drawer { background:#f8f9fc; border-top:1px solid #f0f2f8; padding:14px 18px; }
  .od-title { font-size:.76rem; font-weight:700; color:#1a1d2e; margin-bottom:10px; }
  .od-module-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:8px; margin-bottom:10px; }
  .od-module { display:flex; align-items:center; gap:8px; padding:7px 10px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; font-size:.76rem; font-weight:500; cursor:pointer; transition:all .13s; user-select:none; }
  .od-module.on  { border-color:#10c98e; background:#f0fdf8; color:#0f1123; }
  .od-module.off { border-color:#e0e4f0; background:white; color:#9094a8; }
  .od-module .dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .od-module.on  .dot { background:#10c98e; } .od-module.off .dot { background:#d1d5db; }
  .od-actions { display:flex; gap:8px; flex-wrap:wrap; }
  .oaa-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .oaa-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#7c3aed; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  @media (max-width:768px) {
    .oaa-title { font-size:1.1rem; }
    .module-grid { grid-template-columns:1fr; }
    .od-module-grid { grid-template-columns:1fr 1fr; }
  }
`;

const inits = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';

export default function OwnerAdminAccess() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [subTab,       setSubTab]       = useState('role'); // role | individual
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [adminModules, setAdminModules] = useState([]);
  const [admins,       setAdmins]       = useState([]);
  const [search,       setSearch]       = useState('');
  const [openDrawer,   setOpenDrawer]   = useState(null); // userId
  const [draftModules, setDraftModules] = useState({});   // userId -> modules[]

  const load = () => {
    setLoading(true);
    api.get(`/owner/branches/${schoolId}/admin-access`)
      .then(r => { setAdminModules(r.data.adminModules || []); setAdmins(r.data.admins || []); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [schoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRoleModule = async (key) => {
    const updated = adminModules.includes(key) ? adminModules.filter(k => k !== key) : [...adminModules, key];
    try {
      const { data } = await api.put(`/owner/branches/${schoolId}/admin-access/role`, { modules: updated });
      setAdminModules(data.adminModules);
      toast.success('Access updated');
    } catch { toast.error('Failed to update'); }
  };

  const setAllRole = async (enable) => {
    const updated = enable ? ADMIN_MODULES.map(m => m.key) : [];
    try {
      const { data } = await api.put(`/owner/branches/${schoolId}/admin-access/role`, { modules: updated });
      setAdminModules(data.adminModules);
      toast.success(enable ? 'All modules enabled' : 'All modules disabled');
    } catch { toast.error('Failed'); }
  };

  const openUserDrawer = (userId, currentModules) => {
    if (openDrawer === userId) { setOpenDrawer(null); return; }
    setOpenDrawer(userId);
    setDraftModules(prev => ({ ...prev, [userId]: currentModules || [...adminModules] }));
  };

  const toggleDraftModule = (userId, key) => {
    setDraftModules(prev => {
      const cur = prev[userId] || [];
      return { ...prev, [userId]: cur.includes(key) ? cur.filter(k=>k!==key) : [...cur, key] };
    });
  };

  const saveUserOverride = async (userId) => {
    try {
      setSaving(true);
      await api.put(`/owner/branches/${schoolId}/admin-access/user`, { userId, modules: draftModules[userId] });
      toast.success('Override saved');
      setOpenDrawer(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const removeUserOverride = async (userId) => {
    try {
      setSaving(true);
      await api.delete(`/owner/branches/${schoolId}/admin-access/user`, { data: { userId } });
      toast.success('Override removed — using defaults');
      setOpenDrawer(null);
      load();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const filtered = admins.filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return a.name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s);
  });

  if (loading) return (
    <div className="oaa"><style>{style}</style>
      <div className="oaa-loading"><div className="spinner"/>Loading…</div>
    </div>
  );

  return (
    <div className="oaa"><style>{style}</style>
      <button className="oaa-back" onClick={() => navigate('/owner/branches')}>
        <ArrowLeftIcon />Back to My Branches
      </button>

      <div className="oaa-header">
        <div>
          <div className="oaa-title">Admin Access Control</div>
          <div className="oaa-sub">Give this branch's admin(s) all access, some access, or no access</div>
        </div>
      </div>

      <div className="sub-tabs">
        {[
          { key:'role',       label:'Default (All Admins)' },
          { key:'individual', label:'Individual Override' },
        ].map(st => (
          <button key={st.key} className={`sub-tab ${subTab===st.key?'active':''}`}
            onClick={() => { setSubTab(st.key); setOpenDrawer(null); }}>
            {st.label}
          </button>
        ))}
      </div>

      {subTab === 'role' && (
        <div className="role-card">
          <div className="role-card-head">
            <div>
              <div className="role-card-title">Default Access — All Admins</div>
              <div className="role-card-sub">Applies to every admin in this branch unless individually overridden</div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAllRole(false)}>Disable All</button>
              <button className="btn btn-purple btn-sm" onClick={() => setAllRole(true)}>Enable All</button>
            </div>
          </div>
          <div className="module-grid">
            {ADMIN_MODULES.map(m => {
              const enabled = adminModules.includes(m.key);
              return (
                <div key={m.key} className={`module-item ${enabled?'enabled':'disabled'}`}>
                  <div className="module-item-left">
                    <span className="module-icon">{m.icon}</span>
                    <div>
                      <div className="module-label">{m.label}</div>
                      <div className="module-desc">{m.desc}</div>
                    </div>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={enabled} onChange={() => toggleRoleModule(m.key)} />
                    <span className="toggle-slider" />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {subTab === 'individual' && (
        <>
          <div className="search-wrap">
            <MagnifyingGlassIcon />
            <input className="search-input" placeholder="Search admins…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="panel">
            <div className="panel-head">
              <div className="panel-title">Admins ({filtered.length})</div>
              <div style={{ fontSize:'.72rem', color:'#9094a8' }}>Click Configure to set an admin's own access</div>
            </div>

            {filtered.length === 0 ? (
              <div className="oaa-empty">No admins found.</div>
            ) : filtered.map(person => {
              const userId = String(person.userId || person._id);
              const isOpen = openDrawer === userId;
              const draft  = draftModules[userId] || (person.modules || [...adminModules]);

              return (
                <div key={userId}>
                  <div className="person-row">
                    <div style={{ display:'flex', alignItems:'center', gap:12, flex:1, minWidth:0 }}>
                      <div className="p-avatar">{inits(person.name)}</div>
                      <div style={{ minWidth:0 }}>
                        <div className="p-name">{person.name}{!person.isActive && ' (inactive)'}</div>
                        <div className="p-meta">{person.email}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                      {person.hasOverride
                        ? <span className="p-badge override">✏ Individual Override</span>
                        : <span className="p-badge default">Using Default</span>}
                      <button className="btn btn-ghost btn-sm" onClick={() => openUserDrawer(userId, person.modules || [...adminModules])}>
                        {isOpen ? 'Close' : 'Configure'}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="override-drawer">
                      <div className="od-title">Individual Access — {person.name}</div>
                      <div className="od-module-grid">
                        {ADMIN_MODULES.map(m => {
                          const on = draft.includes(m.key);
                          return (
                            <div key={m.key} className={`od-module ${on ? 'on' : 'off'}`}
                              onClick={() => toggleDraftModule(userId, m.key)}>
                              <span className="dot" /><span>{m.icon} {m.label}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({ ...prev, [userId]: ADMIN_MODULES.map(m => m.key) }))}>Select All</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({ ...prev, [userId]: [] }))}>Clear All</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({ ...prev, [userId]: [...adminModules] }))}>Reset to Default</button>
                      </div>
                      <div className="od-actions">
                        <button className="btn btn-purple btn-sm" onClick={() => saveUserOverride(userId)} disabled={saving}>
                          {saving ? 'Saving…' : 'Save Override'}
                        </button>
                        {person.hasOverride && (
                          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => removeUserOverride(userId)}>
                            <ArrowPathIcon />Remove Override
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenDrawer(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
